// help/ChatHelp.js

export default function ChatHelp() {
  return (
    <div className="space-y-8">

      {/* =======================================================
          HEADER
      ======================================================== */}

      <div>
        <h2 className="text-2xl font-bold">
          Chat
        </h2>

        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          Metisel Chat lets you communicate securely through direct
          messages and group conversations. You can share media,
          send voice messages, reply, forward, edit, pin, and
          schedule messages, create groups, and post Status updates.
        </p>
      </div>

      {/* =======================================================
          STARTING A CONVERSATION
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Starting a Conversation
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open the <strong>Chats</strong> tab.
          </li>

          <li>
            Tap or click <strong>Connect</strong> to discover people you
            want to chat with.
          </li>

          <li>
            Select a contact to open your conversation.
          </li>

          <li>
            Type your message in the message box and press
            <strong> Send</strong>.
          </li>

          <li>
            Your conversations automatically appear in your chat list.
          </li>
        </ul>
      </section>

      {/* =======================================================
          GROUPS
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Creating a Group
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open the Chats screen.
          </li>

          <li>
            Tap the <strong>⋮</strong> menu.
          </li>

          <li>
            Choose <strong>Create Group</strong>.
          </li>

          <li>
            Enter a group name.
          </li>

          <li>
            Select the members you want to add.
          </li>

          <li>
            Confirm to create your group.
          </li>

          <li>
            Group administrators can manage members and group
            information later.
          </li>
        </ul>
      </section>

      {/* =======================================================
          MEDIA
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Sending Photos, Videos and Files
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Tap the <strong>Attachment</strong> button beside the message
            box.
          </li>

          <li>
            Select a photo, video, audio recording or document.
          </li>

          <li>
            Preview your file before sending.
          </li>

          <li>
            Add an optional caption if needed.
          </li>

          <li>
            Press <strong>Send</strong> to deliver your media.
          </li>
        </ul>
      </section>

      {/* =======================================================
          VOICE MESSAGES
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Sending Voice Messages
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Tap the <strong>microphone</strong> icon (shown when the
            message box is empty) to start recording.
          </li>

          <li>
            While recording, tap <strong>Pause</strong> to pause and
            preview your recording before sending.
          </li>

          <li>
            Tap the <strong>mic</strong> icon again to resume a paused
            recording.
          </li>

          <li>
            Tap the <strong>trash</strong> icon at any time to cancel and
            discard the recording.
          </li>

          <li>
            Tap <strong>Send</strong> to deliver your voice message.
          </li>
        </ul>
      </section>

      {/* =======================================================
          REPLIES
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Replying to Messages
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Swipe a message or use the message options to reply.
          </li>

          <li>
            Your reply includes a preview of the original message.
          </li>

          <li>
            Replies help keep conversations organised,
            especially in busy group chats.
          </li>
        </ul>
      </section>

      {/* =======================================================
          MENTIONS
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Mentioning People in Groups
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Type <strong>@</strong> in a group chat to open the member
            picker.
          </li>

          <li>
            Keep typing to filter members by name.
          </li>

          <li>
            Use the <strong>arrow keys</strong> and <strong>Enter</strong>
            {" "}(or tap a name) to insert the mention.
          </li>

          <li>
            Mentioned members are notified that you tagged them in the
            conversation.
          </li>
        </ul>
      </section>

      {/* =======================================================
          SELECTING & MANAGING MESSAGES
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Selecting and Managing Messages
        </h3>

        <p className="mt-3 text-sm">
          Long-press (or tap, once selection has started) a message to
          select it. You can select multiple messages at once, and the
          available actions appear at the top of the chat.
        </p>

        <h4 className="mt-4 font-medium">
          Copy
        </h4>

        <ul className="mt-2 list-disc pl-6 space-y-2 text-sm">
          <li>
            Select one or more text messages and tap <strong>Copy</strong>
            {" "}to copy their text to your clipboard.
          </li>
        </ul>

        <h4 className="mt-5 font-medium">
          Forward
        </h4>

        <ul className="mt-2 list-disc pl-6 space-y-2 text-sm">
          <li>
            Select one or more messages and tap <strong>Forward</strong>.
          </li>

          <li>
            Choose the contacts or groups you want to forward the
            messages to.
          </li>
        </ul>

        <h4 className="mt-5 font-medium">
          Edit
        </h4>

        <ul className="mt-2 list-disc pl-6 space-y-2 text-sm">
          <li>
            Select a single message you sent, open the
            <strong> ⋮ more options</strong> menu, and choose
            <strong> Edit</strong>.
          </li>

          <li>
            Update the text and confirm to save your changes.
          </li>

          <li>
            Scheduled messages can also be edited before they are
            delivered.
          </li>
        </ul>

        <h4 className="mt-5 font-medium">
          Delete
        </h4>

        <ul className="mt-2 list-disc pl-6 space-y-2 text-sm">
          <li>
            Select one or more messages and tap <strong>Delete</strong>.
          </li>

          <li>
            Choose <strong>Delete for me</strong> to remove the message
            only from your view.
          </li>

          <li>
            Choose <strong>Delete for everyone</strong> to remove a
            message you sent (or, for group admins, any message) for all
            participants.
          </li>

          <li>
            Deleting a scheduled message cancels it so it is never sent.
          </li>
        </ul>
      </section>

      {/* =======================================================
          PINNING
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Pinning Messages
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Select a single message, open the
            <strong> ⋮ more options</strong> menu, and choose
            <strong> Pin message</strong>.
          </li>

          <li>
            Pick how long the message should stay pinned: 24 hours,
            7 days, 30 days, or 60 days.
          </li>

          <li>
            Pinned messages appear in a banner at the top of the chat.
            Tap the banner to jump to the message.
          </li>

          <li>
            If more than one message is pinned, tapping the banner
            cycles through them.
          </li>

          <li>
            In direct messages, either participant can unpin a message.
            In groups, only admins can unpin.
          </li>
        </ul>
      </section>

      {/* =======================================================
          SCHEDULED MESSAGES
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Scheduling a Message
        </h3>

        <p className="mt-3 text-sm">
          Metisel allows you to schedule both new and existing
          messages for future delivery.
        </p>

        <h4 className="mt-4 font-medium">
          Schedule a New Message
        </h4>

        <ul className="mt-2 list-disc pl-6 space-y-2 text-sm">
          <li>
            Type your message or attach photos, videos or files.
          </li>

          <li>
            Press and hold the <strong>Send</strong> button (or
            right-click it on desktop).
          </li>

          <li>
            Choose one or more recipients to send it to — the chat
            you're currently in is pre-selected, but you can add more
            contacts or groups. Search to find people quickly, then tap
            <strong> Next</strong>.
          </li>

          <li>
            Pick a delivery date and time. Use a quick-pick shortcut
            (In 1 hour, In 3 hours, Tomorrow 9am, In 1 day) or set a
            custom time.
          </li>

          <li>
            Tap <strong>Schedule send</strong> to confirm.
          </li>
        </ul>

        <h4 className="mt-5 font-medium">
          Schedule an Existing Message
        </h4>

        <ul className="mt-2 list-disc pl-6 space-y-2 text-sm">
          <li>
            Select a message that has already been sent or received.
          </li>

          <li>
            Choose the <strong>Schedule send</strong> option from the
            message actions.
          </li>

          <li>
            Select the date and time you want the message to be
            sent again.
          </li>

          <li>
            Confirm to create the scheduled copy.
          </li>
        </ul>

        <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">
          Scheduled messages remain visible in the conversation until
          they are delivered. Before delivery, they can be edited,
          rescheduled or cancelled.
        </p>
      </section>

      {/* =======================================================
          STATUS
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Status Updates
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open the <strong>Status</strong> section from the Chats page.
          </li>

          <li>
            Share text, photos or videos with your contacts.
          </li>

          <li>
            View updates posted by people you follow.
          </li>

          <li>
            Status updates automatically disappear after their
            visibility period expires.
          </li>
        </ul>
      </section>

      {/* =======================================================
          CHAT OPTIONS
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Chat Options
        </h3>

        <p className="mt-3 text-sm">
          Tap the <strong>⋮</strong> menu in the chat header for more
          options.
        </p>

        <h4 className="mt-4 font-medium">
          Direct Messages
        </h4>

        <ul className="mt-2 list-disc pl-6 space-y-2 text-sm">
          <li>
            <strong>Clear Chat</strong> — removes all messages from your
            view.
          </li>

          <li>
            <strong>Report</strong> — flags the conversation for review.
          </li>

          <li>
            <strong>Block</strong> — prevents the user from contacting
            you.
          </li>
        </ul>

        <h4 className="mt-5 font-medium">
          Groups
        </h4>

        <ul className="mt-2 list-disc pl-6 space-y-2 text-sm">
          <li>
            <strong>Group Info</strong> — view members and group details.
            You can also tap the group avatar or name to open this.
          </li>

          <li>
            <strong>Clear Chat</strong> — removes all messages from your
            view.
          </li>

          <li>
            <strong>Exit Group</strong> — removes you from the group. You
            will need to be re-added to rejoin.
          </li>
        </ul>
      </section>

      {/* =======================================================
          TIPS
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Tips
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Keep your profile information up to date so contacts can
            easily recognise you.
          </li>

          <li>
            Use replies to keep discussions easy to follow.
          </li>

          <li>
            Create groups for families, friends, communities or teams.
          </li>

          <li>
            Share photos and documents directly inside conversations
            instead of switching to another app.
          </li>

          <li>
            Pin important messages so they're easy to find later.
          </li>

          <li>
            Schedule important messages so they are delivered at the
            right time, even if you are offline.
          </li>

         <li>
  Messages on Metisel are protected using end-to-end
  encryption.
</li>

<li>
  If a chat becomes unencrypted, clear your browser history and
  sign in to Metisel again to restore a clean encrypted session.
</li>
        </ul>
      </section>

    </div>
  );
}